import useFetch from "../hooks/useFetch";
import { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
} from "@mui/material";
import dayjs from "dayjs";
import CustomDialog from "../components/ui/CustomDialog";
import StockSettings from "../components/StockSettings";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import Badge from "@mui/material/Badge";
import { useMediaQuery, useTheme } from "@mui/material";
import axios from "axios";

function Dashboard() {
  const fetchData = useFetch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);
  const [isSale, setIsSale] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleChange = (event) => {
    setIsSale(event.target.value);
  };

  const getData = async () => {
    const response = await fetchData("/api/stock/dashboard");
    if (response) setData(response);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  function countOutdatedOrMissingDeliveries(purchasedOrders) {
    const now = new Date();
    return purchasedOrders.filter((order) => {
      return order.deliveryDate === null || new Date(order.deliveryDate) < now;
    }).length;
  }

  const getColumnVisibilityModel = () => {
    const alwaysHidden = {
      leadTime: false,
      shippingTime: false,
      safetyStock: false,
      moq: false,
      unitsPerCarton: false,
    };

    if (isSmallScreen) {
      return {
        ...Object.keys(
          columns.reduce((acc, col) => {
            acc[col.field] = false;
            return acc;
          }, {}),
        ).reduce((acc, field) => {
          acc[field] = false;
          return acc;
        }, {}),
        referenceCode: true,
        daysUntilReorders: true,
      };
    }

    return columns.reduce((acc, col) => {
      acc[col.field] = alwaysHidden[col.field] === false ? false : true;
      return acc;
    }, {});
  };

  const columns = [
    { field: "ean", headerName: "EAN", width: 125 },
    {
      field: "referenceCode",
      headerName: "SKU",
      width: isSmallScreen ? null : 150,
      flex: isSmallScreen ? 1 : null,
      renderCell: (params) => {
        return params.row.referenceCode;
      },
      valueGetter: (value, params) => {
        return params.order;
      },
    },
    { field: "leadTime", headerName: "Productietijd" },
    { field: "shippingTime", headerName: "Verscheeptijd" },
    { field: "safetyStock", headerName: "Marge" },
    { field: "moq", headerName: "MOQ" },
    { field: "unitsPerCarton", headerName: "Eenheden per doos" },
    {
      field: "picqerPurchasedStock",
      headerName: "Onderweg",
      type: "number",
      width: 100,
      renderCell: (params) => {
        const count = countOutdatedOrMissingDeliveries(
          params.row.purchasedOrders,
        );
        return (
          <Box>
            {params.row.purchasedOrders.some((order) => order.gap > 0)
              ? "☢️"
              : ""}
            {params.row.picqerPurchasedStock}
            {count > 0 && (
              <Badge
                badgeContent={count}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.6rem",
                    height: 14,
                    minWidth: 14,
                  },
                  ml: 1,
                }}
              >
                <DirectionsBoatIcon sx={{ fontSize: 18 }} color="action" />
              </Badge>
            )}
          </Box>
        );
      },
      valueGetter: (value, params) => {
        return params.picqerPurchasedStock;
      },
    },
    {
      field: "picqerStock",
      headerName: "Magazijn",
      type: "number",
      width: 100,
    },
    {
      field: "fbbStock",
      headerName: "Lvb",
      type: "number",
      width: 100,
    },
    {
      field: "virtualStock",
      headerName: "Virtuele voorraad",
      renderHeader: () => (
        <HeaderComponent title={"Virtuele"} subtitle={"voorraad"} />
      ),

      type: "number",
      width: 100,
      renderCell: (params) => params.row.virtualStock || 0,
    },
    {
      field: "Reorder Amount",
      headerName: "Reorder point",
      renderHeader: () => (
        <HeaderComponent title={"Reorder"} subtitle={"point"} />
      ),
      type: "number",
      valueGetter: (value, params) => {
        return (
          params[isSale ? "saleAnalysis" : "forecastAnalysis"]?.reorderPoint ||
          0
        );
      },
      renderCell: (params) => (
        <FlexibleComponent
          main={
            params.row[isSale ? "saleAnalysis" : "forecastAnalysis"]
              ?.reorderPoint || 0
          }
          sub={
            params.row[!isSale ? "saleAnalysis" : "forecastAnalysis"]
              ?.reorderPoint || 0
          }
        />
      ),
    },
    {
      field: isSale ? "sales" : "forecast",
      headerName: isSale ? "Verkopen" : "Prognose",
      renderHeader: () => (
        <HeaderComponent
          title={isSale ? "Verkopen" : "Prognose"}
          subtitle={"per 28 dagen"}
        />
      ),
      type: "number",
      renderCell: (params) => (
        <FlexibleComponent
          main={params.row[isSale ? "sales" : "forecast"] || 0}
          sub={params.row[!isSale ? "sales" : "forecast"] || 0}
        />
      ),
    },
    {
      field: "fbbStockDays",
      headerName: "LVB voorraaddagen",
      renderHeader: () => (
        <HeaderComponent title={"LVB"} subtitle={"voorraaddagen"} />
      ),
      type: "number",
      renderCell: (params) => {
        const fbbDaysForecast =
          params.row["forecastAnalysis"]?.fbbStockDays ?? 0;
        const fbbDaysSales = params.row["saleAnalysis"]?.fbbStockDays ?? 0;
        const min = Number(process.env.REACT_APP_MINIMUM_FBB_STOCKDAYS);
        const sales = params.row.sales;

        const isRed =
          sales > 0 ? fbbDaysForecast < min || fbbDaysSales < min : false;

        return (
          <Box>
            <FlexibleComponent
              main={`${`${isRed ? "⚠️" : ""}${
                params.row[isSale ? "saleAnalysis" : "forecastAnalysis"]
                  ?.fbbStockDays ?? 0
              }`}`}
              sub={
                params.row[!isSale ? "saleAnalysis" : "forecastAnalysis"]
                  ?.fbbStockDays ?? 0
              }
            />
          </Box>
        );
      },
      valueGetter: (value, params) => {
        return (
          params[isSale ? "saleAnalysis" : "forecastAnalysis"]?.fbbStockDays ||
          0
        );
      },
    },
    {
      field: "daysUntilSellout",
      headerName: "Uitverkocht over",
      type: "number",
      width: 125,
      renderHeader: () => (
        <HeaderComponent
          title={"Uitverkocht over"}
          subtitle={isSale ? "Verkoopanalyse" : "Prognoseanalyse"}
        />
      ),
      valueGetter: (value, params) => {
        return (
          params[isSale ? "saleAnalysis" : "forecastAnalysis"]
            .daysTillSellout || 0
        );
      },
      renderCell: (params) => {
        return `${params.value} dagen`;
      },
    },
    {
      field: "sellOutDate",
      headerName: "Datum uitverkocht",
      type: "number",
      width: 125,
      renderHeader: () => (
        <HeaderComponent
          title={"Datum uitverkocht"}
          subtitle={isSale ? "Verkoopanalyse" : "Prognoseanalyse"}
        />
      ),
      valueGetter: (value, params) => {
        const date =
          params[isSale ? "saleAnalysis" : "forecastAnalysis"].selloutDate;

        return date ? dayjs(date).unix() : null;
      },
      renderCell: (params) => {
        const date =
          params.row[isSale ? "saleAnalysis" : "forecastAnalysis"]?.selloutDate;
        return date ? dayjs(date).format("DD-MM-YY") : "-";
      },
    },
    {
      field: "daysUntilReorders",
      headerName: "Dagen tot uitverkoop",
      renderHeader: () => (
        <HeaderComponent
          title={"Bestellen over"}
          subtitle={isSale ? "Verkoopanalyse" : "Prognoseanalyse"}
        />
      ),
      type: "number",
      valueGetter: (value, params) => {
        return (
          params[isSale ? "saleAnalysis" : "forecastAnalysis"]
            ?.daysTillReorder || 999
        );
      },
      renderCell: (params) => (
        <Box
          sx={{
            color:
              params.row[isSale ? "saleAnalysis" : "forecastAnalysis"]
                .daysTillReorder < 0 && params.row.reorderNotification
                ? "red"
                : "",
          }}
        >
          {params.row[isSale ? "saleAnalysis" : "forecastAnalysis"]
            .daysTillReorder || 0}

          <sup
            style={{
              fontSize: "7px",
              marginLeft: "1px",
            }}
          >
            {params.row[!isSale ? "saleAnalysis" : "forecastAnalysis"]
              .daysTillReorder || 0}
          </sup>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      renderHeader: () => (
        <HeaderComponent
          title={"Status"}
          subtitle={isSale ? "Verkoopanalyse" : "Prognoseanalyse"}
        />
      ),
      width: 50,
      valueGetter: (value, params) => {
        return params[isSale ? "saleAnalysis" : "forecastAnalysis"]
          ?.daysTillReorder < 0
          ? true
          : false;
      },
      renderCell: (params) => {
        if (!params.row.reorderNotification) return <>☑️</>;
        return (
          <Box
            sx={{
              color:
                params.row["saleAnalysis"].daysTillReorder < 0 ||
                params.row["forecastAnalysis"].daysTillReorder < 0
                  ? "red"
                  : "",
            }}
          >
            {params.row["saleAnalysis"].daysTillReorder < 0 ||
            params.row["forecastAnalysis"].daysTillReorder < 0
              ? "⚠️"
              : "✅"}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Acties",
      renderCell: (params) => (
        <IconButton
          size={"small"}
          onClick={() =>
            window.open(
              `https://partner.bol.com/sdd/assortment-new/product/${params.row.productId}/sales-insight/`,
              "_blank",
            )
          }
        >
          <OpenInNewIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ),
    },
  ];

  function HeaderComponent({ title, subtitle }) {
    return (
      <Box>
        <Typography sx={{ fontSize: "10px" }}>{title}</Typography>
        <Typography sx={{ fontSize: "9px", color: "gray" }}>
          {subtitle}
        </Typography>
      </Box>
    );
  }

  function FlexibleComponent({ main, sub }) {
    return (
      <Box>
        {main}
        <sup
          style={{
            fontSize: "7px",
          }}
        >
          {sub}
        </sup>
      </Box>
    );
  }

  const [companyData, setCompanyData] = useState([]);

  async function getCompanyOrders() {
    try {
      const response = await axios.get(
        "https://hook.eu2.make.com/2g2tehzng260s7zahyuh9mnf0c755r8h",
      );

      const data = response.data;
      setCompanyData(Array.isArray(data) ? data : []);
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    getCompanyOrders();
  }, []);
  return (
    <Grid container rowSpacing={1}>
      <CustomDialog title={"Voorraad instellingen"} />
      <Grid
        item
        size={12}
        sx={{
          justifyContent: "end",
          display: "flex",
        }}
      >
        <FormControl fullWidth size="small">
          <InputLabel>Prognose type</InputLabel>
          <Select
            value={isSale}
            label="Prognose type"
            onChange={handleChange}
            sx={{ width: { xs: "100%" } }}
          >
            <MenuItem value={true}>Verkopen</MenuItem>
            <MenuItem value={false}>Prognose</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12} sx={{ height: "85vh" }}>
        {data.length > 0 ? (
          <DataGrid
            rows={data}
            columns={columns}
            getRowId={(row) => row._id}
            density="compact"
            columnHeaderHeight={75}
            onRowDoubleClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
            initialState={{
              columns: {
                columnVisibilityModel: getColumnVisibilityModel(),
              },
              sorting: isSmallScreen
                ? {
                    sortModel: [
                      {
                        field: "daysUntilReorders",
                        sort: "asc",
                      },
                    ],
                  }
                : undefined,
            }}
          />
        ) : (
          <LinearProgress />
        )}
      </Grid>
      <CustomDialog
        open={open}
        onClose={handleClose}
        title={"Voorraad instellingen"}
      >
        <StockSettings row={selectedRow} refresh={getData} />
      </CustomDialog>
      <Grid>
        <Box>
          {companyData.length > 0 &&
            companyData.map((order, index) => (
              <Box
                key={index}
                mb={2}
                p={2}
                border="1px solid #ccc"
                borderRadius={2}
              >
                <Typography>
                  <strong>Naam:</strong> {order[1]} {order[2]}
                </Typography>
                <Typography>
                  <strong>Bedrijf:</strong> {order[3]}
                </Typography>
                <Typography>
                  <strong>Datum:</strong>{" "}
                  {dayjs(order[4]).format("DD-MM-YYYY HH:mm")}
                </Typography>
                <Typography>
                  <strong>Email:</strong> {order[5]}
                </Typography>
                <Typography>
                  <strong>Product:</strong> {order[6]}
                </Typography>
              </Box>
            ))}
        </Box>
      </Grid>
    </Grid>
  );
}

export default Dashboard;
